import { useEffect, useState } from "react";
import { api } from "../lib/api";

const ACCESS_BADGE = {
  operator: { className: "ok",  label: "Operator" },
  admin:    { className: "warn", label: "Admin" },
  confidential: { className: "bad", label: "Confidential" },
  "operator + confidential": { className: "warn", label: "Operator · Confidential salary" },
};

function FieldRow({ f }) {
  const isObj = f.type === "object";
  const opts = f.options || (f.subfields ? null : null);

  return (
    <>
      <tr>
        <td>
          <span style={{fontFamily:'var(--mono)', fontSize:12}}>{f.key}</span>
          {f.pii && <span className="badge bad" style={{marginLeft:6}}>PII</span>}
          {f.locked && <span className="badge warn" style={{marginLeft:6}}>locked</span>}
          {f.derived && <span className="badge teal" style={{marginLeft:6}}>derived</span>}
        </td>
        <td><span className="dept-name">{f.label}</span>{f.hint ? <div className="dept-sub" style={{marginTop:2}}>{f.hint}</div> : null}</td>
        <td><span style={{fontFamily:'var(--mono)', fontSize:11, color:'var(--text-2)'}}>{f.type}{f.format ? ` · ${f.format}` : ''}{f.currency ? ` · ${f.currency}` : ''}</span></td>
        <td>{f.required ? <span className="badge bad">required</span> : <span style={{fontSize:11, color:'var(--text-3)'}}>optional</span>}</td>
        <td>
          {opts && opts.length > 0 ? (
            <div style={{display:'flex', flexWrap:'wrap', gap:4}}>
              {opts.map(o => <span key={o} className="meta-chip" style={{fontFamily:'var(--mono)', fontSize:10.5}}>{o}</span>)}
            </div>
          ) : isObj ? (
            <span style={{fontSize:11, color:'var(--text-3)', fontStyle:'italic'}}>JSON · see sub-fields below</span>
          ) : (
            <span style={{fontSize:11, color:'var(--text-3)'}}>—</span>
          )}
        </td>
      </tr>
      {isObj && f.subfields && f.subfields.map(sf => (
        <tr key={`${f.key}-${sf.key}`} style={{background:'var(--bg-elev)'}}>
          <td style={{paddingLeft:36}}><span style={{fontFamily:'var(--mono)', fontSize:11, color:'var(--text-3)'}}>↳ {f.key}.{sf.key}</span></td>
          <td style={{fontSize:13}}>{sf.label}{sf.hint ? <div className="dept-sub" style={{marginTop:2}}>{sf.hint}</div> : null}</td>
          <td><span style={{fontFamily:'var(--mono)', fontSize:11, color:'var(--text-2)'}}>{sf.type}{sf.format ? ` · ${sf.format}` : ''}</span></td>
          <td>{sf.required ? <span className="badge bad">required</span> : <span style={{fontSize:11, color:'var(--text-3)'}}>—</span>}</td>
          <td>
            {sf.options ? (
              <div style={{display:'flex', flexWrap:'wrap', gap:4}}>
                {sf.options.slice(0, 12).map(o => <span key={o} className="meta-chip" style={{fontFamily:'var(--mono)', fontSize:10.5}}>{o}</span>)}
                {sf.options.length > 12 && <span style={{fontSize:10.5, color:'var(--text-3)'}}>+{sf.options.length - 12} more</span>}
              </div>
            ) : <span style={{fontSize:11, color:'var(--text-3)'}}>—</span>}
          </td>
        </tr>
      ))}
    </>
  );
}

export default function Schema() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let alive = true;
    api.get("/reference/options")
      .then(({data}) => { if (alive) setData(data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!data) return <div style={{padding:40,textAlign:'center'}}><div className="spinner"/></div>;

  const visibleTables = data.tables.filter(t =>
    !filter || t.code.includes(filter.toLowerCase()) ||
    t.title.toLowerCase().includes(filter.toLowerCase()) ||
    (t.fields || []).some(f => (f.key + ' ' + f.label).toLowerCase().includes(filter.toLowerCase()))
  );

  return (
    <div data-testid="view-schema">
      <div className="topbar">
        <div>
          <h1 className="pg-title">Data Dictionary</h1>
          <div className="pg-sub">Canonical catalogue of every attribute across the 10 normalised tables · A1 through G</div>
        </div>
        <div className="topbar-meta"><span className="meta-chip">{data.tables.length} tables</span></div>
      </div>

      <div className="search-row">
        <div className="search-box">
          <span style={{color:'var(--text-3)'}}>⌕</span>
          <input
            placeholder="Filter by table code, title, or attribute…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            data-testid="schema-filter"
          />
        </div>
      </div>

      {visibleTables.map(t => {
        const badge = ACCESS_BADGE[t.access] || { className:"teal", label: t.access };
        return (
          <div className="card" key={t.code} style={{marginBottom:18}} data-testid={`table-${t.code}`}>
            <div className="card-title" style={{alignItems:'flex-start'}}>
              <div>
                <div style={{display:'flex', gap:10, alignItems:'baseline'}}>
                  <span style={{fontFamily:'var(--mono)', fontSize:13, color:'var(--gold)'}}>TABLE {t.code.toUpperCase()}</span>
                  <span style={{fontFamily:'var(--serif)', fontSize:18}}>{t.title}</span>
                </div>
                <div className="dept-sub" style={{marginTop:6, fontSize:12.5}}>{t.purpose}</div>
              </div>
              <span className={`badge ${badge.className}`} style={{marginLeft:'auto'}}>{badge.label}</span>
            </div>

            {t.locked ? (
              <div style={{padding:'14px 4px', color:'var(--text-3)', fontSize:13, display:'flex', gap:10, alignItems:'center'}}>
                <span style={{color:'var(--rose)'}}>⚑</span>
                Hidden — your role ({t.access}) cannot view this table's attributes.
              </div>
            ) : (
              <div style={{overflowX:'auto'}}>
                <table className="data">
                  <thead>
                    <tr>
                      <th style={{width:220}}>Key</th>
                      <th>Label</th>
                      <th style={{width:140}}>Type</th>
                      <th style={{width:100}}>Required</th>
                      <th>Options / Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(t.fields || []).map(f => <FieldRow key={f.key} f={f}/>)}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      <div className="grid grid-2">
        <div className="card">
          <div className="card-title">Faculty Status — State Machine</div>
          <div style={{display:'flex', flexWrap:'wrap', gap:6, marginBottom:14}}>
            {data.status_state_machine.states.map(s => (
              <span key={s} className="meta-chip" style={{fontFamily:'var(--mono)'}}>{s}</span>
            ))}
          </div>
          <table className="data" style={{fontSize:12}}>
            <thead><tr><th>From</th><th>Allowed Transitions</th></tr></thead>
            <tbody>
              {Object.entries(data.status_state_machine.transitions).map(([from, tos]) => (
                <tr key={from}>
                  <td><span className="dept-name">{from}</span></td>
                  <td>
                    {tos.length === 0
                      ? <span style={{color:'var(--text-3)', fontStyle:'italic', fontSize:11.5}}>terminal — no further transitions</span>
                      : tos.map(t => <span key={t} className="meta-chip" style={{marginRight:4, fontFamily:'var(--mono)', fontSize:10.5}}>→ {t}</span>)
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card">
          <div className="card-title">Indirect Signals (derived, never entered)</div>
          {data.indirect_signals.map((s, i) => (
            <div key={i} style={{padding:'10px 0', borderBottom:'1px solid var(--line-soft)', fontSize:13}}>
              <div style={{fontFamily:'var(--mono)', fontSize:11.5, color:'var(--text-2)'}}>{s.rule}</div>
              <div style={{marginTop:4}}>⇒ <strong style={{color:'var(--text-1)'}}>{s.implies}</strong></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
